import React, { useRef, useEffect, useState, useCallback } from 'react'
import expressLogoUrl from 'url:./canvases/logo.svg'

const ICON_STRIP = [
  { icon: '⌕', label: 'Search' },
  { icon: '+', label: 'Add\ncontent' },
  { icon: 'T', label: 'Text' },
  { icon: '↑', label: 'Upload' },
  { icon: '⊡', label: 'Your stuff' },
  { icon: '◈', label: 'Brands' },
  { icon: '⊞', label: 'Templates' },
  { icon: '◐', label: 'Theme' },
  { icon: '⊕', label: 'Add-ons' }
]

function findSvgRoot (container) {
  if (!container) return null
  if (container.tagName && container.tagName.toLowerCase() === 'svg') return container
  return container.querySelector('svg')
}

function hexToRgb (hex) {
  const h = (hex && hex.replace('#', '')) || ''
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 20, g: 115, b: 230 }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  }
}

function resolvePickedElement (svgRoot, target) {
  if (!svgRoot || !target || !svgRoot.contains(target)) return null
  let el = target
  while (el && el !== svgRoot) {
    if (el.id) return el
    el = el.parentElement
  }
  const closest = typeof target.closest === 'function' ? target.closest('[id]') : null
  if (closest && svgRoot.contains(closest) && closest.id) return closest
  return null
}

export function MockExpressChrome ({
  canvas,
  modifiedSvgUrl,
  modifiedSvgText,
  selectedPanel,
  isLinking,
  linkingPanel,
  onSvgLinkPick
}) {
  const svgContainerRef = useRef(null)
  const wrapperRef = useRef(null)
  const canvasAreaRef = useRef(null)
  const [overlayRect, setOverlayRect] = useState(null)
  const [hoverRect, setHoverRect] = useState(null)
  const [linkingAnchorRect, setLinkingAnchorRect] = useState(null)

  // When the selected panel changes, find its linked node and compute overlay position
  useEffect(() => {
    if (!selectedPanel || !svgContainerRef.current || !wrapperRef.current) {
      setOverlayRect(null)
      return
    }
    const nodeId = selectedPanel.linkedNodeIds?.[0]
    if (!nodeId) { setOverlayRect(null); return }

    const timer = setTimeout(() => {
      try {
        const container = svgContainerRef.current
        const wrapper = wrapperRef.current
        if (!container || !wrapper) return

        const el = container.querySelector(`#${CSS.escape(nodeId)}`)
        if (!el) { setOverlayRect(null); return }

        const elRect = el.getBoundingClientRect()
        const wrapperRect = wrapper.getBoundingClientRect()
        setOverlayRect({
          left: elRect.left - wrapperRect.left,
          top: elRect.top - wrapperRect.top,
          width: elRect.width,
          height: elRect.height
        })
      } catch (_) {
        setOverlayRect(null)
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [selectedPanel, modifiedSvgText])

  // While link mode is armed, keep the existing linked node visibly marked (even when hovering elsewhere)
  useEffect(() => {
    if (!isLinking) {
      setLinkingAnchorRect(null)
      return
    }
    const nodeId = linkingPanel?.linkedNodeIds?.[0]
    if (!nodeId) {
      setLinkingAnchorRect(null)
      return
    }

    function measureAnchor () {
      const container = svgContainerRef.current
      const wrapper = wrapperRef.current
      if (!container || !wrapper) return
      try {
        const el = container.querySelector(`#${CSS.escape(nodeId)}`)
        if (!el) {
          setLinkingAnchorRect(null)
          return
        }
        const elRect = el.getBoundingClientRect()
        const wrapperRect = wrapper.getBoundingClientRect()
        setLinkingAnchorRect({
          left: elRect.left - wrapperRect.left,
          top: elRect.top - wrapperRect.top,
          width: elRect.width,
          height: elRect.height
        })
      } catch (_) {
        setLinkingAnchorRect(null)
      }
    }

    const timer = setTimeout(measureAnchor, 50)
    const wrap = wrapperRef.current
    wrap?.addEventListener('scroll', measureAnchor, { passive: true })
    window.addEventListener('resize', measureAnchor, { passive: true })
    return () => {
      clearTimeout(timer)
      wrap?.removeEventListener('scroll', measureAnchor)
      window.removeEventListener('resize', measureAnchor)
    }
  }, [isLinking, linkingPanel?.linkedNodeIds?.[0], linkingPanel?.color, modifiedSvgText])

  useEffect(() => {
    if (!isLinking) setHoverRect(null)
  }, [isLinking])

  // Prevent focus/scroll-into-view and wheel scrolling over the SVG while linking (keeps layout stable)
  const handleLinkingMouseDownCapture = useCallback((e) => {
    if (!isLinking) return
    const svgRoot = findSvgRoot(svgContainerRef.current)
    if (svgRoot && svgRoot.contains(e.target)) {
      e.preventDefault()
    }
  }, [isLinking])

  // Block all scroll gestures on the mock canvas column while linking (CSS also sets overflow: hidden)
  useEffect(() => {
    const area = canvasAreaRef.current
    if (!area || !isLinking) return
    const blockScroll = (e) => {
      e.preventDefault()
    }
    area.addEventListener('wheel', blockScroll, { passive: false, capture: true })
    area.addEventListener('touchmove', blockScroll, { passive: false, capture: true })
    return () => {
      area.removeEventListener('wheel', blockScroll, { capture: true })
      area.removeEventListener('touchmove', blockScroll, { capture: true })
    }
  }, [isLinking])

  const updateHoverFromPoint = useCallback((clientX, clientY) => {
    const container = svgContainerRef.current
    const wrapper = wrapperRef.current
    const svgRoot = findSvgRoot(container)
    if (!isLinking || !modifiedSvgText || !svgRoot || !wrapper) {
      setHoverRect(null)
      return
    }
    const els = document.elementsFromPoint(clientX, clientY)
    let picked = null
    for (const node of els) {
      if (!svgRoot.contains(node)) continue
      const withId = resolvePickedElement(svgRoot, node)
      if (withId) {
        picked = withId
        break
      }
    }
    if (!picked) {
      setHoverRect(null)
      return
    }
    const elRect = picked.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    setHoverRect({
      left: elRect.left - wrapperRect.left,
      top: elRect.top - wrapperRect.top,
      width: elRect.width,
      height: elRect.height
    })
  }, [isLinking, modifiedSvgText])

  const handleWrapperMouseMove = useCallback((e) => {
    if (!isLinking) {
      setHoverRect(null)
      return
    }
    updateHoverFromPoint(e.clientX, e.clientY)
  }, [isLinking, updateHoverFromPoint])

  const handleWrapperMouseLeave = useCallback(() => {
    setHoverRect(null)
  }, [])

  const handleWrapperClick = useCallback((e) => {
    if (!isLinking || !onSvgLinkPick) return
    const container = svgContainerRef.current
    const svgRoot = findSvgRoot(container)
    if (!svgRoot || !modifiedSvgText) return
    if (!svgRoot.contains(e.target)) return

    const picked = resolvePickedElement(svgRoot, e.target)
    e.preventDefault()
    e.stopPropagation()
    if (!picked || !picked.id) return

    onSvgLinkPick(picked.id)
  }, [isLinking, modifiedSvgText, onSvgLinkPick])

  if (!canvas) return null

  const linkColor = linkingPanel?.color || '#1473e6'
  const linkRgb = hexToRgb(linkColor)

  return (
    <div className='mock-express'>
      <div className='mock-express-topbar'>
        <div className='mock-express-logo'>
          <img
            className='mock-express-logo-img'
            src={expressLogoUrl}
            alt=''
            width={32}
            height={32}
            draggable={false}
          />
        </div>
        <span className='mock-express-wordmark'>Adobe Express</span>
        <div className='mock-express-topbar-sep' />
        <span className='mock-express-topbar-menu'>File</span>
        <span className='mock-express-topbar-title'>{canvas.label}</span>
        <div className='mock-express-topbar-actions'>
          <span className='mock-express-topbar-btn'>Edit page</span>
          <span className='mock-express-topbar-btn'>Resize</span>
          <span className='mock-express-topbar-btn download'>Download</span>
        </div>
      </div>

      <div className='mock-express-body'>
        <div className='mock-express-iconstrip'>
          {ICON_STRIP.map((item) => (
            <div key={item.label} className='mock-express-icon-item' title={item.label}>
              <span className='mock-express-icon-glyph'>{item.icon}</span>
              <span className='mock-express-icon-label'>{item.label}</span>
            </div>
          ))}
        </div>

        <div
          ref={canvasAreaRef}
          className={`mock-express-canvas-area${isLinking ? ' mock-express-canvas-area--linking' : ''}`}
        >
          <div className='mock-express-canvas-toolbar'>
            <span className='mock-express-canvas-action'>✏ Edit page</span>
            <span className='mock-express-canvas-action'>⤢ Resize</span>
          </div>
          <div
            ref={wrapperRef}
            className={`mock-express-svg-wrapper${isLinking ? ' mock-express-svg-wrapper--linking' : ''}`}
            style={{ position: 'relative' }}
            onMouseDownCapture={handleLinkingMouseDownCapture}
            onMouseMove={handleWrapperMouseMove}
            onMouseLeave={handleWrapperMouseLeave}
            onClick={handleWrapperClick}
            role='presentation'
          >
            {modifiedSvgText
              ? (
                <div
                  ref={svgContainerRef}
                  className='mock-express-svg-img'
                  dangerouslySetInnerHTML={{ __html: modifiedSvgText }}
                  style={{ display: 'block' }}
                />
                )
              : (
                <img
                  ref={svgContainerRef}
                  className='mock-express-svg-img'
                  src={modifiedSvgUrl || canvas.svgUrl}
                  alt={canvas.label}
                  draggable={false}
                />
                )}

            {linkingAnchorRect && isLinking && (
              <div
                className='canvas-link-anchor-overlay'
                style={{
                  position: 'absolute',
                  left: linkingAnchorRect.left,
                  top: linkingAnchorRect.top,
                  width: linkingAnchorRect.width,
                  height: linkingAnchorRect.height,
                  outline: `3px solid ${linkColor}`,
                  outlineOffset: 1,
                  backgroundColor: `rgba(${linkRgb.r},${linkRgb.g},${linkRgb.b},0.12)`,
                  pointerEvents: 'none',
                  zIndex: 8,
                  borderRadius: 2,
                  boxShadow: `0 0 0 1px ${linkColor}55`
                }}
              />
            )}

            {hoverRect && isLinking && (
              <div
                className='canvas-link-hover-overlay'
                style={{
                  position: 'absolute',
                  left: hoverRect.left,
                  top: hoverRect.top,
                  width: hoverRect.width,
                  height: hoverRect.height,
                  outline: `2px solid ${linkColor}`,
                  outlineOffset: 0,
                  backgroundColor: `rgba(${linkRgb.r},${linkRgb.g},${linkRgb.b},0.18)`,
                  boxShadow: `0 0 0 1px ${linkColor}99, inset 0 0 0 1px ${linkColor}55`
                }}
              />
            )}

            {overlayRect && selectedPanel && (
              <div
                className='canvas-selection-overlay'
                style={{
                  position: 'absolute',
                  left: overlayRect.left,
                  top: overlayRect.top,
                  width: overlayRect.width,
                  height: overlayRect.height,
                  outline: `3px solid ${selectedPanel.color}`,
                  outlineOffset: '2px',
                  pointerEvents: 'none',
                  zIndex: 10,
                  borderRadius: 2,
                  boxShadow: `0 0 0 1px ${selectedPanel.color}44`
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
